// src/components/CookieBanner/state.ts
import { atom } from 'recoil';
import {
  CROCS_COOKIE_CONSENT_STORAGE_KEY,
  DEFAULT_CONSENT_OPTIONS,
  type ConsentParams,
} from '../constants';
import normalizeConsentParams from '../utils/normalizeConsentParams';

/**
 * Safely load consent from localStorage (browser only).
 * - Falls back to DEFAULT_CONSENT_OPTIONS if nothing stored or parse fails.
 * - Always returns a normalized ConsentParams object.
 */
export const loadStoredConsent = (): ConsentParams => {
  if (typeof window === 'undefined') {
    return DEFAULT_CONSENT_OPTIONS;
  }

  try {
    const raw = window.localStorage.getItem(CROCS_COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return DEFAULT_CONSENT_OPTIONS;

    const parsed = JSON.parse(raw) as Partial<ConsentParams>;
    return normalizeConsentParams(parsed as ConsentParams);
  } catch {
    return DEFAULT_CONSENT_OPTIONS;
  }
};

/**
 * Recoil atom holding the current Google Consent Mode parameters
 * (ad_storage, ad_user_data, ad_personalization, analytics_storage).
 *
 * Source of truth at runtime:
 *  - Initialized from localStorage via loadStoredConsent().
 *  - Updated by cookie banner handlers (createConsentHandlers → persistAndEmitConsentFromCookie).
 */
export const cookieConsentStateAtom = atom<ConsentParams>({
  key: 'cookieConsentState',
  default: loadStoredConsent(),
});
